import { FC, Suspense, SyntheticEvent, useMemo, useRef } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { selectActiveStudentFocus } from '../../redux/reducers/focusSlice';
import { isTargetBlocked } from '../../businessLogicService/focusService';
import { Unit } from '../../models/Unit';
import { DashboardSkillsByTargetQuery$variables } from '../../graphQLOperations/__generated__/DashboardSkillsByTargetQuery.graphql';
import DashboardChart from './DashboardChart';
import { useNavigate } from 'react-router-dom';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import { chartAssignValues } from '../../businessLogicService/chartAssignValues';
import DashboardRecentHistory from './StudentRecentHistory';
import UnitCard from './UnitCard';
import BlockedUnitTooltip, { BlockedUnitTooltipHandle } from './BlockedUnitTooltip';
import { StudentDashboardAssignments } from './StudentDashboardAssignments';
import { useStudentSkillStates } from '../../custom-hooks/useStudentSkillStates';
import { selectActiveFocusUnit } from '../../redux/reducers/focusSlice';
import { selectUser } from '../../redux/reducers/userSlice';
import { getStudentInitials } from '../../businessLogicService/getStudentInitials';
import { Avatar } from 'primereact/avatar';
import { focusUnitAnchorId, useScrollFocusUnitIntoView } from '../../custom-hooks/useScrollFocusUnitIntoView';
interface Props {
  allUnits: Unit[];
  loadQuerySkillsByTarget: (v: DashboardSkillsByTargetQuery$variables) => void;
  defaultUnit: Unit | null;
}

const UnitsPage: FC<Props> = ({
  allUnits,
  defaultUnit, // Use prop instead of selector
}) => {
  const navigate = useNavigate();
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const focusUnit = useAppSelector(selectActiveFocusUnit);
  const user = useAppSelector(selectUser);
  const activeFocus = useAppSelector(selectActiveStudentFocus);
  const availableUnits = allUnits.filter(unit => unit.targetGroup?.id === targetGroupRedux?.id);
  const blockedTooltipRef = useRef<BlockedUnitTooltipHandle>(null);

  // On load, ease the teacher-set focus unit's card near the top — only when a focus unit exists.
  useScrollFocusUnitIntoView(focusUnit?.id ?? null);

  const { studentSkillStates } = useStudentSkillStates({});

  // Calculate chart values without extension skills (sssWithoutExtension)
  const chartValuesWithoutExtension = useMemo(() => {
    if (!studentSkillStates.length) {
      return { total: 0, values: {}, percent: 0 };
    }

    return {
      total: studentSkillStates.length,
      ...chartAssignValues(studentSkillStates),
    };
  }, [studentSkillStates]);

  // Open a unit on the main dashboard (name-based, matching the "Return" link and focus CTA).
  const openUnit = (unitName: string) => navigate(`/?unitNumber=${unitName}`);

  const onUnitAction = (unitId: string) => {
    const unit = availableUnits.find(u => u.id === unitId);
    if (unit) openUnit(unit.name);
  };

  const openBlockedTooltip = (e: SyntheticEvent) => blockedTooltipRef.current?.toggle(e);
  // The blocked-unit tooltip's CTA sends the student to their focused unit.
  const goToFocus = () => {
    if (activeFocus) openUnit(activeFocus.unit.name);
  };

  const isDefaultUnit = (unit: Unit) => {
    return defaultUnit?.id === unit.id;
  };

  return (
    <>
      <div className="col-10 col-offset-1 flex flex-wrap justify-content-between align-items-center tw-pt-0 tw-mt-2">
        <StudentDashboardAssignments />
      </div>
      <div className="col-10 col-offset-1 tw-flex tw-flex-col sm:tw-flex-row">
        <div className="tw-w-full sm:tw-w-auto tw-py-0 tw-mb-4 sm:tw-mb-0 sm:tw-order-2">
          {studentSkillStates && (
            <Suspense fallback={<HorizontalLoading />}>
              <div className="dashboard-card tw-w-full sm:tw-max-w-96">
                <DashboardChart chartValues={chartValuesWithoutExtension} />
                <DashboardRecentHistory />
              </div>
            </Suspense>
          )}
        </div>
        <div className="!tw-p-0 tw-w-full sm:tw-flex-grow sm:tw-mr-4 sm:tw-order-1">
          <div className="tw-w-full tw-space-y-4">
            {availableUnits.map(unit => {
              const isDefault = isDefaultUnit(unit);
              const isFocused = !!focusUnit && focusUnit.id === unit.id;
              return (
                <UnitCard
                  key={unit.id}
                  id={isFocused ? focusUnitAnchorId(unit.id) : undefined}
                  unit={unit}
                  selected={isDefault}
                  focused={isFocused}
                  focusAccessory={
                    <Avatar
                      image={user?.picture}
                      label={getStudentInitials(user) || undefined}
                      shape="circle"
                      className="tw-border-solid tw-rounded-2xl tw-border-t-2 tw-border-r-2 tw-border-b-4 tw-border-l-2 tw-border-[#EBE4DC] tw-bg-[#D9C2F9] tw-relative tw-left-[-5px]"
                    />
                  }
                  blocked={isTargetBlocked(unit.id, activeFocus)}
                  onExplore={onUnitAction}
                  onBlockedActivate={openBlockedTooltip}
                />
              );
            })}
          </div>
          <BlockedUnitTooltip ref={blockedTooltipRef} onGoToFocus={goToFocus} />
        </div>
      </div>
    </>
  );
};

export default UnitsPage;
